import { memo, useCallback, type Dispatch, type SetStateAction, useState } from 'react';
import { CitiesSearch } from '@widgets/coaches/filter/citiesSearch/index';
import { SCheckboxes, SIconCheckbox } from '@widgets/coaches/filter/styles';
import {
  CoachIcon,
  SecondCheckedCheckboxIcon,
  SecondUnCheckedCheckboxIcon,
} from '@shared/assets/index';
import {
  doCheckArray,
  doDeleteOrAddToSearchParams,
  isIncludeInSearchParams,
} from '@shared/helpers/index';
import { AsideFilter, CountOfSelected, getSelectedCount } from '@shared/ui/aside-filter';
import { CheckboxList } from '@shared/ui/aside-filter/checkbox-list/index.ts';
import { PartitionsList } from '@shared/ui/Filter/SectionList/index.tsx';
import type { AccordionPT } from '@shared/ui/layout/accordion/ui.tsx';
import { Partition, Tag } from '@src/api/common.ts';

const consultationTypes = [
  { title: 'Онлайн', searchParam: 'ON' },
  { title: 'Лично', searchParam: 'OF' },
];

type stringState = [string, Dispatch<SetStateAction<string>>];

export type FilterPT = {
  isOpen: boolean;
  tags: Tag[];
  searchCityState: stringState;
  consultationsTypeState: stringState;
  selectedTagsState: stringState;
  onPartitionChange?: (partitionId: number) => void;
  selectedPartitions?: number[];
  doSelectCity: (id: number) => void;
  resetAllParams: () => void;
  partitions: Partition[];
};

export const Filter = memo(
  ({
    isOpen = true,
    searchCityState,
    resetAllParams,
    doSelectCity,
    consultationsTypeState,
    selectedTagsState,
    selectedPartitions,
    onPartitionChange,
    partitions,
    tags,
  }: FilterPT) => {
    const [isTagsOpen, setIsTagsOpen] = useState(false);
    const [consultationsType, setConsultationsType] = consultationsTypeState;
    const [selectedTags, setSelectedTags] = selectedTagsState;
    const [searchCity, setSearchCity] = searchCityState;

    const onConsultationTypeClick = useCallback(
      (searchParam: string) => {
        setConsultationsType(doDeleteOrAddToSearchParams(searchParam, consultationsType));
      },
      [setConsultationsType, consultationsType]
    );

    const onTagClick = useCallback(
      (id: number) => {
        setSelectedTags(doDeleteOrAddToSearchParams(String(id), selectedTags));
      },
      [selectedTags, setSelectedTags]
    );

    const sectionList = [
      {
        title: 'ПО ГОРОДАМ',
        children: (
          <CitiesSearch search={searchCity} setSearch={setSearchCity} onSelect={doSelectCity} />
        ),
        isOpenInit: Boolean(searchCity),
      },
      {
        title: 'ФОРМАТ',

        children: (
          <SCheckboxes>
            {consultationTypes.map((t) => (
              <SIconCheckbox
                key={t.title}
                label={t.title}
                checked={isIncludeInSearchParams(t.searchParam, consultationsType)}
                onClick={() => onConsultationTypeClick(t.searchParam)}
                checkedIcon={<SecondCheckedCheckboxIcon />}
                unCheckedIcon={<SecondUnCheckedCheckboxIcon />}
              />
            ))}
          </SCheckboxes>
        ),
        isOpenInit: Boolean(consultationsType),
      },
      {
        title: 'СПЕЦИАЛИЗАЦИЯ',
        children: (
          <PartitionsList
            icon={<CoachIcon />}
            partitions={partitions}
            selectedPartitionId={selectedPartitions?.at(0)}
            onPartitionClick={(partitionId) => {
              setSelectedTags('');
              onPartitionChange?.(partitionId);
            }}
          />
        ),
        isOpenInit: Boolean(selectedPartitions?.at(0)) || isTagsOpen,
      },
    ] as AccordionPT[];
    doCheckArray(tags) &&
      sectionList.push({
        title: 'ТЕГИ',
        headerChildrenOnClose: <CountOfSelected count={getSelectedCount(selectedTags)} />,
        children: (
          <CheckboxList
            items={tags}
            onItemClick={onTagClick}
            selectedItems={selectedTags.split(',').map(Number)}
          />
        ),
        onOpen: () => setIsTagsOpen(true),
        onClose: () => setIsTagsOpen(false),
        isOpenInit: Boolean(selectedTags),
      });
    return (
      <>
        <AsideFilter
          onResetFilter={resetAllParams}
          showResetBtn={false}
          isOpen={isOpen}
          sections={sectionList}
        />
      </>
    );
  }
);
